// ExclusionPreviewModal.jsx — BespokeBridge
// Modal for showing contact lists with exclusion details.

import React, { useState, useEffect } from 'react';

function ExclusionPreviewModal({ isOpen, onClose, previewData, modalType, campaignName }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText]   = useState('');
  const [sortField, setSortField]     = useState('displayName');
  const [sortDir, setSortDir]         = useState('asc');
  const perPage = 100;

  useEffect(() => { setCurrentPage(1); }, [filterText]);

  if (!isOpen || !previewData) return null;

  let contactsToShow = [];
  let excludedContacts = [];
  let modalTitle = 'Contact Preview';

  switch (modalType) {
    case 'unsubscribe':
      contactsToShow  = previewData.eligibleCustomers || [];
      excludedContacts = previewData.unsubscribedCustomers || [];
      modalTitle = 'After Excluding Unsubscribed Contacts';
      break;
    case 'frequency':
      contactsToShow  = previewData.eligibleCustomers || [];
      excludedContacts = [
        ...(previewData.unsubscribedCustomers || []),
        ...(previewData.frequencyExcludedCustomers || []),
      ];
      modalTitle = 'After Excluding Frequency Limits';
      break;
    case 'recurrence':
      contactsToShow  = previewData.eligibleCustomers || [];
      excludedContacts = [
        ...(previewData.unsubscribedCustomers || []),
        ...(previewData.frequencyExcludedCustomers || []),
        ...(previewData.recurrenceExcludedCustomers || []),
      ];
      modalTitle = 'After Excluding Previous Recipients';
      break;
    case 'final':
      contactsToShow  = previewData.eligibleCustomers || [];
      excludedContacts = [
        ...(previewData.unsubscribedCustomers || []),
        ...(previewData.frequencyExcludedCustomers || []),
        ...(previewData.recurrenceExcludedCustomers || []),
      ];
      modalTitle = 'Final Contact List (All Exclusions Applied)';
      break;
    default:
      contactsToShow = previewData.eligibleCustomers || [];
  }

  const totalCount   = previewData.totalCount || 0;
  const eligibleCount = contactsToShow.length;
  const excludedCount = excludedContacts.length;
  const coverage = totalCount > 0 ? ((eligibleCount / totalCount) * 100).toFixed(1) : '0.0';

  const filtered = contactsToShow.filter(c => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();
    return (c.displayName || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = (a[sortField] || '').toLowerCase();
    const bv = (b[sortField] || '').toLowerCase();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const paged = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const handleExportCSV = () => {
    const rows = [['Name', 'Type', 'Email']];
    sorted.forEach(c => rows.push([`"${c.displayName || ''}"`, c._type || '', `"${c.email || ''}"`]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().split('T')[0];
    const name = (campaignName || 'campaign').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    a.download = `campaign-recipients-${name}-${date}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="bb-comm-modal-overlay">
      <div className="bb-comm-modal bb-comm-modal--wide" style={{ maxWidth: 720 }}>
        <div className="bb-comm-modal-header">
          <h4 className="bb-comm-modal-title">{modalTitle}</h4>
          <button className="bb-comm-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="bb-comm-modal-body">
          {/* Summary stats */}
          <div className="bb-comm-preview-stats" style={{ marginBottom: 16 }}>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val" style={{ color: '#10B981' }}>{eligibleCount}</div>
              <div className="bb-comm-preview-stat-lbl">Will Receive Campaign</div>
            </div>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val" style={{ color: '#EF4444' }}>{excludedCount}</div>
              <div className="bb-comm-preview-stat-lbl">Excluded</div>
            </div>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val">{totalCount}</div>
              <div className="bb-comm-preview-stat-lbl">Total Contacts</div>
            </div>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val" style={{ color: '#4338CA' }}>{coverage}%</div>
              <div className="bb-comm-preview-stat-lbl">Coverage</div>
            </div>
          </div>

          {/* Exclusion breakdown */}
          {(previewData.unsubscribedCount > 0 || previewData.frequencyExcludedCount > 0 || previewData.recurrenceExcludedCount > 0) && (
            <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
              {previewData.unsubscribedCount > 0 && (
                <div style={{ marginBottom: 4 }}>
                  ✉️ <strong>{previewData.unsubscribedCount}</strong> contact(s) excluded due to unsubscribe preferences
                </div>
              )}
              {previewData.frequencyExcludedCount > 0 && (
                <div style={{ marginBottom: 4 }}>
                  🚫 <strong>{previewData.frequencyExcludedCount}</strong> contact(s) excluded due to frequency limits
                  {previewData.frequencySettings && (
                    <span style={{ color: '#9CA3AF' }}>
                      {' '}(max {previewData.frequencySettings.maxFrequency} per {previewData.frequencySettings.interval})
                    </span>
                  )}
                </div>
              )}
              {previewData.recurrenceExcludedCount > 0 && (
                <div style={{ marginBottom: 4 }}>
                  🔄 <strong>{previewData.recurrenceExcludedCount}</strong> contact(s) excluded as previous recipients
                </div>
              )}
              {modalType === 'final' && previewData.summary && (
                <div style={{ marginTop: 6, fontWeight: 600 }}>
                  Total Exclusion Rate: {previewData.summary.exclusionRate}%
                </div>
              )}
            </div>
          )}

          {eligibleCount > 0 ? (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <input
                  className="bb-form-input"
                  style={{ flex: 1, minWidth: 200 }}
                  placeholder="Search by name or email…"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button className="bb-btn-secondary" onClick={() => setFilterText('')}>Clear</button>
                )}
                <button className="bb-btn-secondary" onClick={handleExportCSV}>📥 Export CSV</button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="bb-table" style={{ width: '100%', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('displayName')} style={{ cursor: 'pointer' }}>
                        Name {sortField === 'displayName' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                      </th>
                      <th>Type</th>
                      <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                        Email {sortField === 'email' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.length === 0 ? (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#9CA3AF', padding: 20 }}>No results match your search.</td></tr>
                    ) : paged.map((c, i) => (
                      <tr key={c.id || i}>
                        <td style={{ fontWeight: 600 }}>{c.displayName || '—'}</td>
                        <td style={{ textTransform: 'capitalize', fontSize: 11, color: '#6B7280' }}>{c._type}</td>
                        <td style={{ color: '#4338CA' }}>{c.email || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 12, fontSize: 13 }}>
                  <button className="bb-btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                  <span style={{ color: '#6B7280' }}>Page {currentPage} of {totalPages}</span>
                  <button className="bb-btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px 20px', color: '#9CA3AF' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
              <div>
                {excludedCount > 0
                  ? 'All targeted contacts have been excluded based on frequency or recurrence settings.'
                  : 'No eligible recipients found for the selected criteria.'}
              </div>
            </div>
          )}
        </div>

        <div className="bb-comm-modal-footer">
          <button className="bb-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default ExclusionPreviewModal;
